import React, { useEffect, useState, useMemo } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelection } from "@/contexts/ActiveCompanyContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SafeFormDialog } from "@/components/SafeFormDialog";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Plus, Pencil, Trash2, UserRound, KeyRound, ShieldOff, Save, Copy, Upload, Search, Users, MoreHorizontal,
  ClipboardList, ArrowUp, ArrowDown, IdCard, Briefcase, PiggyBank, X, FileSpreadsheet, FileText, CalendarDays,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { MONTHS_IT } from "@/lib/constants";
import EmployeeImportDialog from "@/components/EmployeeImportDialog";
import { SuiteCard, SuiteButton, SuiteEyebrow, SuiteBadge } from "@/components/suite";
import { sortCompaniesIt } from "@/lib/sortCompanies";
import { announceShellCompany, companyNameForShell } from "@/lib/shellCompany";
import { RateiDetailGrid } from "@/components/RateiDetail";
import { downloadBlob } from "@/lib/download";
import { formatDateIT } from "@/foundation/formatting";
import { useEffectiveRole } from "@/foundation/context";
import { fmtHours } from "@/foundation/formatting";
import { DurationInput, DurationLabel } from "@/foundation/duration";
import { CounterHoursInput } from "@/components/employees/CounterHoursInput";
import { formatCounterHours } from "@/lib/counterHours";

const empty = () => ({
  first_name: "", last_name: "", fiscal_code: "", email: "", matricola: "",
  hire_date: "",
  qualification: "", level: "",
  residence_address: "", residence_city: "", residence_cap: "", residence_province: "",
  phone_mobile: "",
  employment_type: "full_time",
  initial_balance_hours: 0,
  initial_balance_month: new Date().getMonth() + 1,
  initial_balance_year: new Date().getFullYear(),
  ratei_base_year: null,
  ratei_ferie_iniziale_gg: 0,
  ratei_rol_iniziale_h: 0,
  ratei_ex_fest_iniziale_h: 0,
  ratei_ferie_mensile_gg: 0,
  ratei_rol_mensile_h: 0,
  ratei_ex_fest_mensile_h: 0,
  active: true,
  // v0.99.28 · Fase 1 · Cessazione dipendente
  cessation_date: "",
  cessation_reason: "",
  // v0.99.30-c2-close (C2.1): banca ore per dipendente
  bank_hours_enabled: false,
  bank_hours_start_date: "",
  create_user_account: false, user_email: "", user_password: "",
});

// Etichette leggibili dei motivi di cessazione
const CESSATION_REASON_LABELS = {
  DIMISSIONI: "Dimissioni",
  LICENZIAMENTO: "Licenziamento",
  PENSIONAMENTO: "Pensionamento",
  FINE_CONTRATTO: "Fine contratto",
  ALTRO: "Altro",
};

/** Format ISO YYYY-MM-DD → DD/MM/YYYY */
function fmtIsoIt(iso) {
  return formatDateIT(iso, "");
}

/** Riga label + valore per la scheda profilo read-only. */
function ReadRow({ label, value, mono }) {
  const display = value && String(value).trim() ? value : "—";
  return (
    <div className="flex items-baseline gap-3 py-1 border-b border-suite-neutral-100 last:border-0"
         data-testid={`profile-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[11px] uppercase tracking-wider text-suite-neutral-500 w-40 shrink-0">{label}</div>
      <div className={`text-sm text-suite-neutral-800 ${mono ? "font-mono" : ""}`}>{display}</div>
    </div>
  );
}

/** Header-cell con sort indicator. */
function SortHead({ label, active, dir, onClick, className = "" }) {
  return (
    <button type="button" onClick={onClick}
            className={`inline-flex items-center gap-1 text-xs uppercase tracking-widest font-semibold ${active ? "text-suite-primary" : "text-suite-neutral-600"} hover:text-suite-primary transition-colors ${className}`}>
      {label}
      {active && (dir === "asc" ? <ArrowUp size={11} strokeWidth={2.5} /> : <ArrowDown size={11} strokeWidth={2.5} />)}
    </button>
  );
}

const currentYear = () => new Date().getFullYear();
const currentMonth = () => new Date().getMonth() + 1;

export default function Employees() {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const navigate = useNavigate();
  const isExternalConsultant = effectiveRole === "external_consultant";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useCompanySelection();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty());
  const [initialForm, setInitialForm] = useState(empty());
  const [activeTab, setActiveTab] = useState("anagrafica");
  const [rateiSummary, setRateiSummary] = useState(null);
  const [accrualProfiles, setAccrualProfiles] = useState([]);
  const [newAccrual, setNewAccrual] = useState({
    effective_from: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-01`,
    vacation_monthly: 0,
    vacation_unit: "days",
    rol_monthly_hours: 0,
    ex_fest_monthly_hours: 0,
  });
  const [accrualBusy, setAccrualBusy] = useState(false);
  const [editingAccrualId, setEditingAccrualId] = useState(null);
  // D1 · Export organico
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Filtri + ricerca (client-side, persistenza localStorage)
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(() => {
    try {
      const v = localStorage.getItem("dipendenti_status_filter") || "active";
      // Migration: vecchi valori 'inactive' non hanno più senso, mappa a 'terminated'
      if (v === "inactive") return "terminated";
      return v;
    } catch (_) { return "active"; }
  });
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("last_name");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    try { localStorage.setItem("dipendenti_status_filter", statusFilter); } catch (_) { /* noop */ }
  }, [statusFilter]);

  // Account di accesso
  const [account, setAccount] = useState({ has_account: false });
  const [accEmail, setAccEmail] = useState("");
  const [accUsername, setAccUsername] = useState("");
  const [accPassword, setAccPassword] = useState("");
  const [accBusy, setAccBusy] = useState(false);

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialForm),
    [form, initialForm]
  );

  const load = async () => {
    try {
      if (effectiveRole === "employee") {
        if (!user?.employee_id) { toast.error("Nessun profilo dipendente collegato al tuo account"); return; }
        const { data } = await api.get(`/employees/${user.employee_id}`);
        setItems([data]);
        return;
      }
      if (["admin", "super_admin", "external_consultant"].includes(effectiveRole)) {
        const { data } = await api.get("/companies");
        const sorted = sortCompaniesIt(data);
        setCompanies(sorted);
        if (!companyId && sorted.length > 0) { setCompanyId(sorted[0].id); return; }
      }
      const params = {};
      if (["admin", "super_admin", "external_consultant"].includes(effectiveRole) && companyId) params.company_id = companyId;
      // v0.99.28: filtro server-side per stato cessazione
      // active (default) → attivi + cessazione prevista · all → tutti · terminated → solo cessati
      if (statusFilter && statusFilter !== "active") params.status = statusFilter;
      else params.status = "active";
      const { data } = await api.get("/employees", { params });
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, [companyId, statusFilter]);
  useEffect(() => {
    announceShellCompany(
      user?.company_name || companyNameForShell(companies, companyId),
      companyId,
    );
  }, [companies, companyId, user?.company_name]);

  // D1 · Download export organico (XLSX o PDF)
  const downloadOrganico = async (kind) => {
    if (!companyId) { toast.error("Seleziona un'azienda prima di esportare"); return; }
    const setBusy = kind === "excel" ? setExportingXlsx : setExportingPdf;
    setBusy(true);
    try {
      const res = await api.get(`/exports/organico/${kind}`, {
        params: { company_id: companyId, status: statusFilter },
        responseType: "blob",
      });
      // Estrai filename da Content-Disposition
      const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      const fname = m ? m[1] : `organico.${kind === "excel" ? "xlsx" : "pdf"}`;
      downloadBlob(res.data, fname, res.headers?.["content-type"]);
      toast.success(`Export ${kind === "excel" ? "Excel" : "PDF"} pronto (${fname})`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) toast.error("Non sei autorizzato ad esportare questa azienda");
      else if (status === 404) toast.error("Azienda non trovata");
      else if (status === 400) toast.error("Azienda obbligatoria per l'export");
      else toast.error("Errore durante la generazione dell'export");
    } finally { setBusy(false); }
  };

  const openNew = () => {
    if (["admin", "super_admin"].includes(effectiveRole) && !companyId) { toast.error("Seleziona un'azienda prima"); return; }
    const fresh = empty();
    setForm(fresh); setInitialForm(fresh); setEditingId(null);
    setRateiSummary(null);
    setAccrualProfiles([]);
    setEditingAccrualId(null);
    setActiveTab("anagrafica"); setOpen(true);
    setAccount({ has_account: false }); setAccEmail(""); setAccUsername(""); setAccPassword("");
  };
  const openEdit = (e) => {
    // v0.99.85+: la modifica avviene su pagina dedicata `/dipendenti/:id`.
    navigate(`/dipendenti/${e.id}`);
  };

  const saveAccrualProfile = async () => {
    if (!editingId) return;
    setAccrualBusy(true);
    try {
      await api.post(`/employees/${editingId}/accrual-profiles`, {
        ...newAccrual,
        vacation_monthly: Number(newAccrual.vacation_monthly || 0),
        rol_monthly_hours: Number(newAccrual.rol_monthly_hours || 0),
        ex_fest_monthly_hours: Number(newAccrual.ex_fest_monthly_hours || 0),
      });
      const [{ data: history }, { data: summary }] = await Promise.all([
        api.get(`/employees/${editingId}/accrual-profiles`),
        api.get("/attendance/ratei-summary", {
          params: { employee_id: editingId, year: currentYear(), month: currentMonth() },
        }),
      ]);
      setAccrualProfiles(history.items || []);
      setRateiSummary(summary);
      toast.success("Nuova decorrenza salvata; i valori precedenti restano nello storico");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setAccrualBusy(false);
    }
  };

  const emptyAccrualForm = () => ({
    effective_from: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-01`,
    vacation_monthly: 0,
    vacation_unit: "days",
    rol_monthly_hours: 0,
    ex_fest_monthly_hours: 0,
  });

  const startAccrualEdit = (row) => {
    setEditingAccrualId(row.id);
    setNewAccrual({
      effective_from: String(row.effective_from || "").slice(0, 10),
      vacation_monthly: Number(row.vacation_monthly || 0),
      vacation_unit: row.vacation_unit === "hours" ? "hours" : "days",
      rol_monthly_hours: Number(row.rol_monthly_hours || 0),
      ex_fest_monthly_hours: Number(row.ex_fest_monthly_hours || 0),
    });
  };

  const cancelAccrualEdit = () => {
    setEditingAccrualId(null);
    setNewAccrual(emptyAccrualForm());
  };

  const updateAccrualProfile = async () => {
    if (!editingId || !editingAccrualId) return;
    setAccrualBusy(true);
    try {
      await api.patch(`/employees/${editingId}/accrual-profiles/${editingAccrualId}`, {
        ...newAccrual,
        vacation_monthly: Number(newAccrual.vacation_monthly || 0),
        rol_monthly_hours: Number(newAccrual.rol_monthly_hours || 0),
        ex_fest_monthly_hours: Number(newAccrual.ex_fest_monthly_hours || 0),
      });
      const [{ data: history }, { data: summary }] = await Promise.all([
        api.get(`/employees/${editingId}/accrual-profiles`),
        api.get("/attendance/ratei-summary", {
          params: { employee_id: editingId, year: currentYear(), month: currentMonth() },
        }),
      ]);
      setAccrualProfiles(history.items || []);
      setRateiSummary(summary);
      cancelAccrualEdit();
      toast.success("Decorrenza aggiornata");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setAccrualBusy(false);
    }
  };

  const saveAccount = async () => {
    if (!editingId) return;
    if (!form.email?.trim()) { toast.error("Inserisci prima l'email nell'Anagrafica"); return; }
    if (account.has_account && !accUsername.trim()) { toast.error("Nome utente obbligatorio"); return; }
    if (accPassword && accPassword.length < 6) { toast.error("Password troppo corta (min 6 caratteri)"); return; }
    setAccBusy(true);
    try {
      const body = {
        email: form.email.trim().toLowerCase(),
        username: accUsername.trim() || null,
      };
      if (accPassword) body.password = accPassword;
      const { data } = await api.put(`/employees/${editingId}/account`, body);
      setAccount(data); setAccEmail(form.email.trim().toLowerCase()); setAccUsername(data.username || ""); setAccPassword("");
      toast.success(
        data.linked_existing
          ? "Account esistente collegato senza duplicazioni"
          : (account.has_account ? "Account aggiornato" : "Account creato")
      );
    } catch (e) { toast.error(apiErr(e)); }
    finally { setAccBusy(false); }
  };

  const deleteAccount = async () => {
    if (!editingId || !account.has_account) return;
    if (!window.confirm(
      "Rimuovere l'accesso di questo dipendente al portale?\n\n" +
      "I dati anagrafici e le presenze resteranno invariati. Se l'account è " +
      "esclusivamente dipendente verrà eliminato e il nome utente tornerà " +
      "disponibile. Se possiede anche un ruolo aziendale, verrà soltanto " +
      "scollegato dal dipendente."
    )) return;
    setAccBusy(true);
    try {
      const { data } = await api.delete(`/employees/${editingId}/account`);
      setAccount({ has_account: false }); setAccEmail(""); setAccUsername(""); setAccPassword("");
      toast.success(
        data.account_deleted
          ? "Account dipendente eliminato; nome utente nuovamente disponibile"
          : "Accesso dipendente scollegato; account aziendale conservato"
      );
    } catch (e) { toast.error(apiErr(e)); }
    finally { setAccBusy(false); }
  };

  const generateRandomPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!#@$*";
    let p = "";
    for (let i = 0; i < 12; i++) p += chars.charAt(Math.floor(Math.random() * chars.length));
    setAccPassword(p);
    navigator.clipboard.writeText(p).catch(() => {});
    toast.success("Password generata e copiata negli appunti");
  };

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error("Nome e cognome obbligatori"); return; }
    try {
      const cid = effectiveRole === "client" ? user.company_id : companyId;
      if (editingId) {
        const payload = (effectiveRole === "employee")
          ? {
              first_name: form.first_name, last_name: form.last_name,
              fiscal_code: form.fiscal_code || null,
              email: form.email || null,
              residence_address: form.residence_address || null,
              residence_city: form.residence_city || null,
              residence_cap: form.residence_cap || null,
              residence_province: form.residence_province || null,
              phone_mobile: form.phone_mobile || null,
            }
          : {
              first_name: form.first_name, last_name: form.last_name,
              fiscal_code: form.fiscal_code || null, email: form.email || null,
              matricola: form.matricola || null,
              hire_date: form.hire_date || null,
              qualification: form.qualification || null,
              level: form.level || null,
              residence_address: form.residence_address || null,
              residence_city: form.residence_city || null,
              residence_cap: form.residence_cap || null,
              residence_province: form.residence_province || null,
              phone_mobile: form.phone_mobile || null,
              employment_type: form.employment_type,
              initial_balance_hours: Number(form.initial_balance_hours),
              initial_balance_month: Number(form.initial_balance_month),
              initial_balance_year: Number(form.initial_balance_year),
              ratei_base_year: form.ratei_base_year ? Number(form.ratei_base_year) : null,
              ratei_ferie_iniziale_gg: Number(form.ratei_ferie_iniziale_gg || 0),
              ratei_rol_iniziale_h: Number(form.ratei_rol_iniziale_h || 0),
              ratei_ex_fest_iniziale_h: Number(form.ratei_ex_fest_iniziale_h || 0),
              // v0.99.28 · Fase 1: cessazione
              cessation_date: form.cessation_date || null,
              cessation_reason: form.cessation_reason || null,
              // v0.99.30-c2-close (C2.1): banca ore per dipendente
              bank_hours_enabled: !!form.bank_hours_enabled,
              bank_hours_start_date: form.bank_hours_start_date || null,
            };
        await api.patch(`/employees/${editingId}`, payload);
      } else {
        await api.post("/employees", {
          ...form,
          email: form.email || null,
          user_email: form.email || form.user_email || null,
          company_id: cid,
          initial_balance_hours: Number(form.initial_balance_hours),
        });
      }
      toast.success("Salvato");
      setInitialForm(form);
      setOpen(false);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleActive = async (emp) => {
    try {
      await api.patch(`/employees/${emp.id}`, { active: !emp.active });
      toast.success(emp.active ? "Dipendente disattivato" : "Dipendente riattivato");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (emp) => {
    if (!window.confirm(`Eliminare ${emp.last_name} ${emp.first_name}?\n\n⚠️ Verranno cancellate anche tutte le presenze registrate. L'operazione è IRREVERSIBILE.\n\nSuggerimento: se vuoi solo bloccare l'accesso, usa "Disattiva".`)) return;
    try { await api.delete(`/employees/${emp.id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  // ============ FILTRI + SORT client-side ============
  // Nota v0.99.28: il filtro per stato (active/all/terminated) è ora server-side
  // in `load()`. Qui restano solo ricerca testuale, tipo contratto, sort.
  const filteredItems = useMemo(() => {
    let arr = items;
    if (typeFilter !== "all") arr = arr.filter((e) => e.employment_type === typeFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((e) => {
        const s = `${e.last_name || ""} ${e.first_name || ""} ${e.matricola || ""} ${e.fiscal_code || ""}`.toLowerCase();
        return s.includes(q);
      });
    }
    // sort
    const dir = sortDir === "asc" ? 1 : -1;
    arr = [...arr].sort((a, b) => {
      const av = a[sortBy] || "";
      const bv = b[sortBy] || "";
      if (typeof av === "string") return av.localeCompare(bv, "it", { sensitivity: "base" }) * dir;
      return (av > bv ? 1 : av < bv ? -1 : 0) * dir;
    });
    return arr;
  }, [items, search, typeFilter, sortBy, sortDir]);

  // Conteggi per stato semantico (dal campo `status` restituito dal backend)
  const activeCount = items.filter((e) => e.status === "active" || e.status === "planned").length;
  const ceasedCount = items.filter((e) => e.status === "ceased").length;
  const plannedCount = items.filter((e) => e.status === "planned").length;

  const setSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const cur = { year: currentYear(), month: currentMonth() };
  const employeePresenzeLink = (empId) => `/presenze?employee=${empId}&year=${cur.year}&month=${cur.month}`;

  return (
    <div className="space-y-3 max-w-[1500px]" data-testid="dipendenti-page">
      {/* HEADER 1-riga stile Reference */}
      <div className="flex items-baseline justify-between flex-wrap gap-x-3 gap-y-2">
        <div className="flex items-baseline gap-3 flex-wrap">
          <SuiteEyebrow icon={Users} className="!mb-0">
            {effectiveRole === "employee" ? "Il mio profilo" : "Amministrazione"}
          </SuiteEyebrow>
          <h1 className="text-2xl font-bold tracking-tight text-suite-neutral-800"
              style={{ fontFamily: "Manrope" }}>
            {effectiveRole === "employee" ? "I miei dati" : "Dipendenti"}
          </h1>
          {effectiveRole !== "employee" && (
            <span className="text-sm text-suite-neutral-400">
              · <span className="font-mono font-semibold text-suite-neutral-700">{items.length}</span>
              {statusFilter === "active" ? " attivi" : statusFilter === "terminated" ? " cessati" : " totali"}
              {plannedCount > 0 && statusFilter !== "terminated" && (
                <> · <span className="font-mono font-semibold text-suite-warning" data-testid="planned-count">{plannedCount}</span> cessazione prevista</>
              )}
            </span>
          )}
        </div>
        {effectiveRole !== "employee" && (
          <div className="flex items-center gap-1.5">
            {!isExternalConsultant && (
              <>
                <SuiteButton
                  size="sm" variant="secondary" icon={Upload}
                  onClick={() => setImportOpen(true)}
                  disabled={!companyId && ["admin", "super_admin"].includes(effectiveRole)}
                  data-testid="import-employees-btn"
                >
                  Importa da Excel
                </SuiteButton>
                <SuiteButton size="sm" icon={Plus} onClick={openNew} data-testid="new-employee-btn">
                  Nuovo dipendente
                </SuiteButton>
              </>
            )}
          </div>
        )}
      </div>

      {/* INFO-STRIP: azienda + ricerca + filtri (solo admin/client) */}
      {effectiveRole !== "employee" && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-suite-neutral-200 bg-suite-surface px-3 py-2"
             data-testid="dipendenti-info-strip">
          {["admin", "super_admin", "external_consultant"].includes(effectiveRole) && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest font-semibold text-suite-neutral-500">Azienda</span>
                <Select value={companyId || ""} onValueChange={setCompanyId}>
                  <SelectTrigger className="w-52 h-8 text-xs" data-testid="employees-company-select">
                    <SelectValue placeholder="Seleziona" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-px h-5 bg-suite-neutral-200" />
            </>
          )}
          {/* Search bar */}
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-suite-neutral-400 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per cognome, nome, matricola o C.F. …"
              className="h-8 pl-8 pr-8 text-xs"
              data-testid="dipendenti-search-input"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-suite-neutral-400 hover:text-suite-neutral-700"
                      data-testid="dipendenti-search-clear" title="Pulisci ricerca">
                <X size={12} />
              </button>
            )}
          </div>
          <div className="w-px h-5 bg-suite-neutral-200" />
          {/* Filtro stato */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-suite-neutral-500">Stato</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="dipendenti-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Solo attivi</SelectItem>
                <SelectItem value="all">Attivi + cessati</SelectItem>
                <SelectItem value="terminated">Solo cessati</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Filtro tipo */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-suite-neutral-500">Tipo</span>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-28 h-8 text-xs" data-testid="dipendenti-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="full_time">Full time</SelectItem>
                <SelectItem value="part_time">Part time</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Contatore risultati */}
          {(search || statusFilter !== "all" || typeFilter !== "all") && (
            <div className="ml-auto text-xs text-suite-neutral-500" data-testid="dipendenti-results-count">
              <span className="font-mono font-semibold text-suite-neutral-800">{filteredItems.length}</span> di {items.length}
            </div>
          )}
        </div>
      )}

      {/* TABELLA */}
      {effectiveRole !== "employee" && (
      <SuiteCard padding="none" testid="dipendenti-table-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="bg-white py-2 px-3">
                <SortHead label="Dipendente" active={sortBy === "last_name"} dir={sortDir} onClick={() => setSort("last_name")} />
              </TableHead>
              <TableHead className="bg-white py-2 px-3">
                <span className="text-xs uppercase tracking-widest font-semibold text-suite-neutral-600">Qualifica · Livello</span>
              </TableHead>
              <TableHead className="bg-white py-2 px-3 w-[120px]">
                <SortHead label="Tipo" active={sortBy === "employment_type"} dir={sortDir} onClick={() => setSort("employment_type")} />
              </TableHead>
              <TableHead className="bg-white py-2 px-3 w-[110px]">
                <SortHead label="Stato" active={sortBy === "active"} dir={sortDir} onClick={() => setSort("active")} />
              </TableHead>
              <TableHead className="bg-white py-2 px-3 text-right w-[160px]">
                <span className="text-xs uppercase tracking-widest font-semibold text-suite-neutral-600">Azioni</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.map((e) => (
              <TableRow key={e.id} className="hover:bg-suite-surface-hover" data-testid={`employee-row-${e.id}`}>
                <TableCell className="px-3 py-2">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border ${e.status === "ceased" ? "bg-suite-neutral-100 text-suite-neutral-400 border-suite-neutral-200" : "bg-suite-primary-light text-suite-primary border-suite-primary/25"}`}>
                      <UserRound size={14} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-suite-neutral-800 leading-tight">
                        {e.last_name} {e.first_name}
                      </div>
                      <div className="text-[11px] text-suite-neutral-500 font-mono mt-0.5">
                        {e.matricola ? `Matr. ${e.matricola}` : (e.fiscal_code ? e.fiscal_code : "—")}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="px-3 py-2">
                  <div className="text-sm text-suite-neutral-700">{e.qualification || "—"}</div>
                  {e.level && <div className="text-[11px] text-suite-neutral-500 mt-0.5">{e.level}</div>}
                </TableCell>
                <TableCell className="px-3 py-2">
                  <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium ${e.employment_type === "full_time" ? "bg-suite-secondary-light text-suite-secondary border border-suite-secondary/30" : "bg-suite-accent-light text-suite-accent-hover border border-suite-accent-border"}`}>
                    {e.employment_type === "full_time" ? "Full time" : "Part time"}
                  </span>
                </TableCell>
                <TableCell className="px-3 py-2">
                  {e.status === "ceased" ? (
                    <SuiteBadge tone="neutral" size="sm" data-testid={`badge-ceased-${e.id}`}>
                      Cessato dal {fmtIsoIt(e.cessation_date)}
                    </SuiteBadge>
                  ) : e.status === "planned" ? (
                    <SuiteBadge tone="warning" size="sm" data-testid={`badge-planned-${e.id}`}>
                      Cessazione prevista {fmtIsoIt(e.cessation_date)}
                    </SuiteBadge>
                  ) : (
                    <SuiteBadge tone="ok" size="sm">Attivo</SuiteBadge>
                  )}
                </TableCell>
                <TableCell className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-0.5">
                    <SuiteButton
                      as={Link} to={employeePresenzeLink(e.id)}
                      variant="ghost" size="sm" icon={ClipboardList}
                      data-testid={`view-presenze-${e.id}`}
                      title="Vai alle presenze del mese corrente"
                      className="!px-2"
                    />
                    <SuiteButton
                      variant="ghost" size="sm" icon={Pencil}
                      onClick={() => openEdit(e)}
                      data-testid={`edit-employee-${e.id}`}
                      title={isExternalConsultant ? "Consulta anagrafica completa" : "Modifica dipendente"}
                      className="!px-2"
                    />
                    {!isExternalConsultant && <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button"
                                className="h-8 w-8 rounded-md hover:bg-suite-neutral-100 text-suite-neutral-500 inline-flex items-center justify-center transition-colors"
                                data-testid={`more-employee-${e.id}`} title="Altre azioni">
                          <MoreHorizontal size={15} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {/* NB v0.99.28: rimossa l'azione "Disattiva/Riattiva".
                            Ora lo stato di un dipendente è gestito UNICAMENTE
                            dalla data di cessazione (Contratto tab), evitando
                            stati contraddittori con il flag `active` legacy. */}
                        <DropdownMenuItem onClick={() => del(e)}
                                          className="text-suite-error"
                                          data-testid={`delete-employee-${e.id}`}>
                          <Trash2 size={14} className="mr-2" /> Elimina definitivamente
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filteredItems.length === 0 && items.length > 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-suite-neutral-500 py-8" data-testid="empty-state-filtered">
                  <Search size={28} className="mx-auto mb-2 opacity-40" />
                  <div className="font-semibold text-suite-neutral-700">Nessun risultato</div>
                  <div className="text-xs mt-1">Prova a modificare la ricerca o i filtri</div>
                  <button type="button" onClick={() => { setSearch(""); setStatusFilter("all"); setTypeFilter("all"); }}
                          className="mt-3 text-xs text-suite-primary hover:underline font-semibold"
                          data-testid="reset-filters">
                    Reimposta filtri
                  </button>
                </TableCell>
              </TableRow>
            )}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-suite-neutral-500 py-10" data-testid="empty-state-none">
                  <Users size={32} className="mx-auto mb-3 opacity-40" />
                  <div className="font-semibold text-suite-neutral-700">Nessun dipendente ancora</div>
                  <div className="text-xs mt-1 mb-4">Crea il primo dipendente per iniziare a gestire presenze e banca ore</div>
                  <SuiteButton size="sm" icon={Plus} onClick={openNew} data-testid="empty-create-first-btn">
                    Crea il primo dipendente
                  </SuiteButton>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </SuiteCard>
      )}

      {/* SCHEDA PROFILO read-only (employee) */}
      {effectiveRole === "employee" && items[0] && !open && (
        <SuiteCard testid="my-profile-card">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-suite-primary-light text-suite-primary border border-suite-primary/25 flex items-center justify-center">
                <UserRound size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-suite-neutral-500 font-semibold">
                  Il mio profilo — {items[0].matricola ? `Matricola ${items[0].matricola}` : "Dipendente"}
                </div>
                <div className="text-xl font-bold text-suite-neutral-800" style={{ fontFamily: "Manrope" }}>
                  {items[0].last_name} {items[0].first_name}
                </div>
              </div>
            </div>
            <SuiteButton onClick={() => openEdit(items[0])} data-testid="edit-my-profile-btn" size="sm" icon={Pencil}>
              Variazione
            </SuiteButton>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-suite-primary font-semibold mb-2">Dati anagrafici</div>
              <ReadRow label="Cognome" value={items[0].last_name} />
              <ReadRow label="Nome" value={items[0].first_name} />
              <ReadRow label="Codice fiscale" value={items[0].fiscal_code} mono />
              <ReadRow label="Qualifica" value={items[0].qualification} />
              <ReadRow label="Livello" value={items[0].level} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-suite-primary font-semibold mb-2">Residenza attuale</div>
              <ReadRow label="Indirizzo" value={items[0].residence_address} />
              <ReadRow label="Città" value={items[0].residence_city} />
              <ReadRow label="CAP" value={items[0].residence_cap} mono />
              <ReadRow label="Provincia" value={items[0].residence_province} mono />
            </div>
            <div className="md:col-span-2">
              <div className="text-[11px] uppercase tracking-widest text-suite-primary font-semibold mb-2 mt-3">Contatti</div>
              <ReadRow label="Telefono cellulare" value={items[0].phone_mobile} mono />
            </div>
          </div>
          <div className="mt-5 pt-4 border-t border-suite-neutral-200 text-[11px] text-suite-neutral-500 leading-relaxed">
            Se i dati non sono corretti clicca <b>Variazione</b> per aggiornarli.
            I dati contrattuali (matricola, qualifica, livello, ratei, saldi) sono di competenza dell&apos;amministratore.
          </div>
        </SuiteCard>
      )}

      {/* DIALOG MODIFICA con TABS */}
      <SafeFormDialog
        open={open} onOpenChange={setOpen} isDirty={isDirty} size="lg"
        title={editingId
          ? (effectiveRole === "employee" ? "Il mio profilo" : `Modifica · ${form.last_name} ${form.first_name}`)
          : "Nuovo dipendente"}
        description="Le modifiche saranno salvate solo cliccando su Salva."
      >
        <form onSubmit={(ev) => { ev.preventDefault(); save(); }} data-testid="employee-form">
          {effectiveRole === "employee" && (
            <div className="mb-4 px-3 py-2.5 rounded-md border border-suite-secondary/30 bg-suite-secondary-light/60 text-xs text-suite-secondary">
              <strong className="font-semibold">Il mio profilo</strong> — puoi aggiornare autonomamente i tuoi dati anagrafici e la residenza.
              I dati contrattuali (matricola, qualifica, livello, ratei, saldi) sono di competenza dell&apos;amministratore.
            </div>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className={`grid ${effectiveRole === "employee" ? "grid-cols-2" : (editingId ? "grid-cols-5" : "grid-cols-4")} w-full mb-4`}>
              <TabsTrigger value="anagrafica" data-testid="tab-anagrafica">
                <IdCard size={13} className="mr-1.5" /> Anagrafica
              </TabsTrigger>
              {effectiveRole !== "employee" && (
                <TabsTrigger value="contratto" data-testid="tab-contratto">
                  <Briefcase size={13} className="mr-1.5" /> Contratto
                </TabsTrigger>
              )}
              {effectiveRole !== "employee" && (
                <TabsTrigger value="banca-ore" data-testid="tab-banca-ore">
                  <PiggyBank size={13} className="mr-1.5" /> Banca ore
                </TabsTrigger>
              )}
              <TabsTrigger value="contatori" data-testid="tab-contatori">
                <CalendarDays size={13} className="mr-1.5" /> Contatori
              </TabsTrigger>
              {editingId && effectiveRole !== "employee" && !isExternalConsultant && (
                <TabsTrigger value="account" data-testid="tab-account">
                  <KeyRound size={13} className="mr-1.5" /> Account portale
                </TabsTrigger>
              )}
            </TabsList>

            {/* === TAB 1: Anagrafica === */}
            <TabsContent value="anagrafica" className="space-y-4 mt-0">
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Cognome *</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="emp-form-lastname" /></div>
                <div><Label className="text-xs">Nome *</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="emp-form-firstname" /></div>
                <div className="md:col-span-2"><Label className="text-xs">Codice fiscale</Label><Input value={form.fiscal_code || ""} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value })} data-testid="emp-form-cf" className="font-mono uppercase" /></div>
                <div className="md:col-span-2">
                  <Label className="text-xs">Email personale</Label>
                  <Input type="email" value={form.email || ""}
                         onChange={(e) => setForm({ ...form, email: e.target.value })}
                         placeholder="Usata per notifiche e recupero password"
                         data-testid="emp-form-email" />
                  <p className="text-[11px] text-suite-neutral-500 mt-1">
                    Non è il nome utente: l'accesso avviene sempre con nome.cognome.
                  </p>
                </div>
              </div>
              <div className="pt-2">
                <div className="text-[11px] uppercase tracking-widest text-suite-neutral-500 font-semibold mb-2">Residenza attuale</div>
                <div className="grid md:grid-cols-6 gap-3">
                  <div className="md:col-span-4"><Label className="text-xs">Indirizzo</Label><Input value={form.residence_address || ""} onChange={(e) => setForm({ ...form, residence_address: e.target.value })} placeholder="Via / Piazza e civico" data-testid="emp-form-address" /></div>
                  <div className="md:col-span-2"><Label className="text-xs">CAP</Label><Input value={form.residence_cap || ""} onChange={(e) => setForm({ ...form, residence_cap: e.target.value })} placeholder="00100" data-testid="emp-form-cap" maxLength={5} className="font-mono" /></div>
                  <div className="md:col-span-4"><Label className="text-xs">Città</Label><Input value={form.residence_city || ""} onChange={(e) => setForm({ ...form, residence_city: e.target.value })} data-testid="emp-form-city" /></div>
                  <div className="md:col-span-2"><Label className="text-xs">Provincia</Label><Input value={form.residence_province || ""} onChange={(e) => setForm({ ...form, residence_province: e.target.value.toUpperCase().slice(0,2) })} placeholder="RM" data-testid="emp-form-province" maxLength={2} className="font-mono uppercase" /></div>
                </div>
              </div>
              <div className="pt-2">
                <div className="text-[11px] uppercase tracking-widest text-suite-neutral-500 font-semibold mb-2">Contatti</div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Telefono cellulare</Label><Input type="tel" value={form.phone_mobile || ""} onChange={(e) => setForm({ ...form, phone_mobile: e.target.value })} placeholder="+39 ..." data-testid="emp-form-mobile" /></div>
                </div>
              </div>
            </TabsContent>

            {/* === TAB 2: Contratto === */}
            {effectiveRole !== "employee" && (
              <TabsContent value="contratto" className="space-y-3 mt-0">
                <div className="grid md:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Matricola</Label><Input value={form.matricola || ""} onChange={(e) => setForm({ ...form, matricola: e.target.value })} data-testid="emp-form-matricola" /></div>
                  <div><Label className="text-xs">Data assunzione</Label><Input type="date" value={form.hire_date || ""} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} data-testid="emp-form-hire-date" /></div>
                  <div><Label className="text-xs">Qualifica</Label><Input value={form.qualification || ""} onChange={(e) => setForm({ ...form, qualification: e.target.value })} placeholder="es. Operaio specializzato" data-testid="emp-form-qualification" /></div>
                  <div><Label className="text-xs">Livello</Label><Input value={form.level || ""} onChange={(e) => setForm({ ...form, level: e.target.value })} placeholder="es. 3° livello CCNL" data-testid="emp-form-level" /></div>
                  <div>
                    <Label className="text-xs">Tipo</Label>
                    <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                      <SelectTrigger data-testid="emp-form-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_time">Full time</SelectItem>
                        <SelectItem value="part_time">Part time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* -------- Cessazione rapporto (v0.99.28 · Fase 1) -------- */}
                <div className="border-t border-suite-neutral-200 pt-3 mt-2">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-suite-neutral-500 mb-2">
                    Cessazione rapporto
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Data cessazione</Label>
                      <Input
                        type="date"
                        value={form.cessation_date || ""}
                        onChange={(e) => setForm({ ...form, cessation_date: e.target.value })}
                        data-testid="emp-form-cessation-date"
                      />
                      <div className="text-[10px] text-suite-neutral-500 mt-1">
                        Ultimo giorno di rapporto. Lascia vuoto se rapporto attivo.
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">
                        Motivo cessazione
                        {form.cessation_date && <span className="text-suite-error"> *</span>}
                      </Label>
                      <Select
                        value={form.cessation_reason || ""}
                        onValueChange={(v) => setForm({ ...form, cessation_reason: v })}
                        disabled={!form.cessation_date}
                      >
                        <SelectTrigger data-testid="emp-form-cessation-reason">
                          <SelectValue placeholder="Seleziona motivo…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="DIMISSIONI">Dimissioni</SelectItem>
                          <SelectItem value="LICENZIAMENTO">Licenziamento</SelectItem>
                          <SelectItem value="PENSIONAMENTO">Pensionamento</SelectItem>
                          <SelectItem value="FINE_CONTRATTO">Fine contratto</SelectItem>
                          <SelectItem value="ALTRO">Altro</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="text-[10px] text-suite-neutral-500 mt-1">
                        {form.cessation_date
                          ? "Obbligatorio quando la data di cessazione è impostata."
                          : "Attivo quando è impostata una data di cessazione."}
                      </div>
                    </div>
                  </div>
                  {form.cessation_date && (
                    <div className="mt-2 flex items-center justify-between gap-3 p-2 rounded-md bg-suite-neutral-50 border border-suite-neutral-200">
                      <div className="text-xs text-suite-neutral-700">
                        {new Date(form.cessation_date) >= new Date(new Date().toISOString().slice(0, 10))
                          ? <>Cessazione <b>prevista</b> per il {fmtIsoIt(form.cessation_date)}. Il dipendente resta operativo fino a quella data.</>
                          : <>Cessazione già avvenuta il {fmtIsoIt(form.cessation_date)}. Escluso dalle elaborazioni dal giorno successivo.</>
                        }
                      </div>
                      <SuiteButton
                        variant="ghost" size="sm"
                        onClick={() => setForm({ ...form, cessation_date: "", cessation_reason: "" })}
                        data-testid="emp-form-cancel-cessation"
                      >
                        Annulla cessazione
                      </SuiteButton>
                    </div>
                  )}
                </div>
              </TabsContent>
            )}

            {/* === TAB 3: Banca ore === */}
            {effectiveRole !== "employee" && (
              <TabsContent value="banca-ore" className="space-y-3 mt-0">
                {/* v0.99.30-c2-close (C2.1): toggle "Gestione banca ore" per dipendente
                    + decorrenza. Disabilitato se l'azienda ha bank_hours_enabled=false,
                    con messaggio esplicativo; i valori non vengono cancellati (conservano
                    l'intent per eventuale riattivazione azienda). */}
                {(() => {
                  const selectedCompany = companies.find((c) => c.id === (form.company_id || (effectiveRole === "client" ? user.company_id : "")));
                  const companyBankOff = selectedCompany && selectedCompany.bank_hours_enabled === false;
                  const today = new Date().toISOString().slice(0, 10);
                  return (
                    <div
                      className="px-3 py-3 rounded-md border border-suite-neutral-200 bg-suite-surface"
                      data-testid="emp-form-bank-hours-section"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={!!form.bank_hours_enabled}
                              disabled={companyBankOff}
                              onCheckedChange={(v) => setForm({
                                ...form,
                                bank_hours_enabled: v,
                                // Quando si attiva ex-novo, proponi la data corrente.
                                bank_hours_start_date: v && !form.bank_hours_start_date ? today : form.bank_hours_start_date,
                              })}
                              data-testid="emp-form-bank-hours-toggle"
                            />
                            <Label className="text-sm font-semibold">Gestione banca ore</Label>
                          </div>
                          <p className="text-[11px] text-suite-neutral-500 mt-1">
                            {companyBankOff
                              ? "L'azienda selezionata non gestisce la banca ore: il flag è disattivato a livello di sistema. I valori restano memorizzati per eventuale riattivazione futura."
                              : "Nuovi dipendenti nascono senza banca ore. Abilita solo quando previsto contrattualmente."}
                          </p>
                        </div>
                      </div>
                      {form.bank_hours_enabled && !companyBankOff && (
                        <div className="mt-3 grid md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Decorrenza banca ore</Label>
                            <Input
                              type="date"
                              value={form.bank_hours_start_date || ""}
                              onChange={(e) => setForm({ ...form, bank_hours_start_date: e.target.value })}
                              data-testid="emp-form-bank-hours-start-date"
                            />
                            <p className="text-[10px] text-suite-neutral-400 mt-1">
                              La banca ore è attiva solo dalle date &ge; a questa.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div className="text-[11px] uppercase tracking-widest text-suite-neutral-500 font-semibold mb-1">Saldo iniziale banca ore</div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div><DurationLabel className="text-xs" data-testid="emp-form-initial-hours">Ore</DurationLabel><DurationInput allowNegative value={form.initial_balance_hours ?? 0} onCommit={(n) => setForm((f) => ({ ...f, initial_balance_hours: n ?? 0 }))} data-testid="emp-form-initial-hours" /></div>
                  <div>
                    <Label className="text-xs">Mese</Label>
                    <Select value={String(form.initial_balance_month)} onValueChange={(v) => setForm({ ...form, initial_balance_month: Number(v) })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{MONTHS_IT.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Anno</Label><Input type="number" value={form.initial_balance_year} onChange={(e) => setForm({ ...form, initial_balance_year: Number(e.target.value) })} /></div>
                </div>
                {!editingId && (
                  <div className="border-t border-suite-neutral-200 pt-3 mt-3">
                    <div className="flex items-center gap-2">
                      <Switch checked={form.create_user_account} onCheckedChange={(v) => setForm({ ...form, create_user_account: v })} data-testid="emp-form-create-user" />
                      <Label className="text-sm">Crea account di accesso per il dipendente</Label>
                    </div>
                    {form.create_user_account && (
                      <div className="grid md:grid-cols-2 gap-3 mt-2">
                        <div><Label className="text-xs">Email accesso</Label><Input type="email" value={form.user_email} onChange={(e) => setForm({ ...form, user_email: e.target.value })} /></div>
                        <div><Label className="text-xs">Password iniziale</Label><Input type="text" value={form.user_password} onChange={(e) => setForm({ ...form, user_password: e.target.value })} /></div>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>
            )}

            {/* Contatori: visibili a tutti, modificabili solo dai ruoli gestionali. */}
            <TabsContent value="contatori" className="space-y-4 mt-0" data-testid="employee-ratei-tab">
              {editingId && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  <div>
                    <div className="text-xs font-semibold text-suite-neutral-700 mb-2">Ferie</div>
                    <RateiDetailGrid pool={rateiSummary?.ferie_pool} testid="employee-ratei-ferie" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-suite-neutral-700 mb-2">ROL / Permessi</div>
                    <RateiDetailGrid pool={rateiSummary?.rol_pool} testid="employee-ratei-rol" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-suite-neutral-700 mb-2">Ex festività</div>
                    <RateiDetailGrid pool={rateiSummary?.ex_fest_pool} testid="employee-ratei-exfest" />
                  </div>
                </div>
              )}

              {effectiveRole !== "employee" ? (
                <div className="rounded-lg border border-suite-neutral-200 bg-suite-neutral-50 p-4">
                  <div className="mb-3">
                    <div className="text-sm font-semibold text-suite-neutral-800">Valori contrattuali dei contatori</div>
                    <div className="text-xs text-suite-neutral-500">Le modifiche vengono applicate solo cliccando “Salva”.</div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    {accrualProfiles[0]?.vacation_unit === "hours" ? (
                  <div><DurationLabel className="text-xs" data-testid="ratei-ferie-initial">Ferie residue iniziali (h)</DurationLabel><DurationInput value={form.ratei_ferie_iniziale_gg ?? 0} onCommit={(n) => setForm((f) => ({ ...f, ratei_ferie_iniziale_gg: n ?? 0 }))} data-testid="ratei-ferie-initial" /></div>
                ) : (
                  <div><Label className="text-xs">Ferie residue iniziali (gg)</Label><Input type="number" step="0.01" value={form.ratei_ferie_iniziale_gg ?? 0} onChange={(e) => setForm({ ...form, ratei_ferie_iniziale_gg: e.target.value })} data-testid="ratei-ferie-initial" /></div>
                )}
                    <div><Label className="text-xs">ROL residui iniziali (h, 3 decimali)</Label><CounterHoursInput value={form.ratei_rol_iniziale_h ?? 0} onCommit={(n) => setForm((f) => ({ ...f, ratei_rol_iniziale_h: n ?? 0 }))} data-testid="ratei-rol-initial" /></div>
                    <div><Label className="text-xs">Ex festività residue iniziali (h, 3 decimali)</Label><CounterHoursInput value={form.ratei_ex_fest_iniziale_h ?? 0} onCommit={(n) => setForm((f) => ({ ...f, ratei_ex_fest_iniziale_h: n ?? 0 }))} data-testid="ratei-exfest-initial" /></div>
                    {!editingId && <div><Label className="text-xs">Maturazione ferie mensile (gg)</Label><Input type="number" step="0.01" value={form.ratei_ferie_mensile_gg ?? 0} onChange={(e) => setForm({ ...form, ratei_ferie_mensile_gg: e.target.value })} data-testid="ratei-ferie-monthly" /></div>}
                    {!editingId && <div><DurationLabel className="text-xs" data-testid="ratei-rol-monthly">Maturazione ROL mensile (h)</DurationLabel><DurationInput value={form.ratei_rol_mensile_h ?? 0} onCommit={(n) => setForm((f) => ({ ...f, ratei_rol_mensile_h: n ?? 0 }))} data-testid="ratei-rol-monthly" /></div>}
                    {!editingId && <div><DurationLabel className="text-xs" data-testid="ratei-exfest-monthly">Maturazione Ex festività mensile (h)</DurationLabel><DurationInput value={form.ratei_ex_fest_mensile_h ?? 0} onCommit={(n) => setForm((f) => ({ ...f, ratei_ex_fest_mensile_h: n ?? 0 }))} data-testid="ratei-exfest-monthly" /></div>}
                    <div><Label className="text-xs">Anno base per il riporto</Label><Input type="number" value={form.ratei_base_year || ""} onChange={(e) => setForm({ ...form, ratei_base_year: e.target.value ? Number(e.target.value) : null })} placeholder="es. 2026" data-testid="emp-form-ratei-base-year" /></div>
                  </div>
                  {editingId && (
                    <div className="mt-5 border-t border-suite-neutral-200 pt-4">
                      <div className="text-sm font-semibold text-suite-neutral-800">Maturazioni mensili storicizzate</div>
                      <div className="mb-3 text-xs text-suite-neutral-500">
                        La nuova decorrenza non modifica né elimina i valori applicati nei periodi precedenti.
                      </div>
                      <div className="overflow-x-auto rounded-md border border-suite-neutral-200 bg-white">
                        <table className="w-full text-xs">
                          <thead className="bg-suite-neutral-50"><tr>
                            <th className="px-3 py-2 text-left">Dal</th>
                            <th className="px-3 py-2 text-left">Ferie</th>
                            <th className="px-3 py-2 text-left">ROL</th>
                            <th className="px-3 py-2 text-left">Ex festività</th>
                            <th className="px-3 py-2 text-right">Azioni</th>
                          </tr></thead>
                          <tbody>{accrualProfiles.map((row) => (
                            <tr key={`${row.id || "legacy"}-${row.effective_from}`} className="border-t border-suite-neutral-100">
                              <td className="px-3 py-2">{fmtIsoIt(row.effective_from)}</td>
                              <td className="px-3 py-2">{row.vacation_unit === "hours" ? fmtHours(row.vacation_monthly || 0) : Number(row.vacation_monthly || 0).toFixed(2)} {row.vacation_unit === "hours" ? "h" : "gg"}</td>
                              <td className="px-3 py-2">{formatCounterHours(row.rol_monthly_hours || 0)} h</td>
                              <td className="px-3 py-2">{formatCounterHours(row.ex_fest_monthly_hours || 0)} h</td>
                              <td className="px-3 py-2 text-right">
                                <SuiteButton type="button" variant="secondary" size="sm" onClick={() => startAccrualEdit(row)}>
                                  Modifica
                                </SuiteButton>
                              </td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                      <div className="mt-4 rounded-lg border border-suite-neutral-200 bg-suite-neutral-50 p-4">
                        <div className="mb-3">
                          <div className="text-sm font-semibold text-suite-neutral-800">
                            {editingAccrualId ? "Modifica decorrenza" : "Nuova decorrenza"}
                          </div>
                          <div className="text-xs text-suite-neutral-500">
                            {editingAccrualId
                              ? "La rettifica aggiorna i calcoli dalla data indicata; le altre decorrenze restano invariate."
                              : "Inserisci i valori validi dalla nuova data, senza modificare i periodi precedenti."}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <div><Label className="text-xs">Data di decorrenza</Label><Input className="w-full" type="date" value={newAccrual.effective_from} onChange={(e) => setNewAccrual({ ...newAccrual, effective_from: e.target.value })} /></div>
                        {newAccrual.vacation_unit === "hours" ? (
                          <div><DurationLabel className="text-xs">Ferie mensili</DurationLabel><DurationInput value={newAccrual.vacation_monthly} onCommit={(n) => setNewAccrual((f) => ({ ...f, vacation_monthly: n ?? 0 }))} /></div>
                        ) : (
                          <div><Label className="text-xs">Ferie mensili</Label><Input className="w-full" type="number" min="0" step="0.01" value={newAccrual.vacation_monthly} onChange={(e) => setNewAccrual({ ...newAccrual, vacation_monthly: e.target.value })} /></div>
                        )}
                        <div><Label className="text-xs">Unità ferie</Label>
                          <Select value={newAccrual.vacation_unit} onValueChange={(value) => setNewAccrual({ ...newAccrual, vacation_unit: value })}>
                            <SelectTrigger className="w-full"><SelectValue /></SelectTrigger><SelectContent>
                              <SelectItem value="days">Giorni</SelectItem><SelectItem value="hours">Ore</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs">ROL mensili (h, 3 decimali)</Label><CounterHoursInput value={newAccrual.rol_monthly_hours ?? 0} onCommit={(n) => setNewAccrual((f) => ({ ...f, rol_monthly_hours: n ?? 0 }))} /></div>
                        <div><Label className="text-xs">Ex festività mensili (h, 3 decimali)</Label><CounterHoursInput value={newAccrual.ex_fest_monthly_hours ?? 0} onCommit={(n) => setNewAccrual((f) => ({ ...f, ex_fest_monthly_hours: n ?? 0 }))} /></div>
                        </div>
                        <div className="mt-4 flex flex-wrap justify-end gap-2">
                          {editingAccrualId && (
                            <SuiteButton type="button" variant="secondary" onClick={cancelAccrualEdit} disabled={accrualBusy}>
                              Annulla modifica
                            </SuiteButton>
                          )}
                          <SuiteButton
                            type="button"
                            onClick={editingAccrualId ? updateAccrualProfile : saveAccrualProfile}
                            disabled={accrualBusy}
                          >
                            {accrualBusy ? "Salvataggio…" : editingAccrualId ? "Salva modifica" : "Aggiungi decorrenza"}
                          </SuiteButton>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-md border border-suite-primary/20 bg-suite-primary/5 px-3 py-2 text-xs text-suite-neutral-600">
                  I contatori sono consultabili in sola lettura. Per eventuali rettifiche rivolgiti all&apos;azienda o allo studio.
                </div>
              )}
            </TabsContent>

            {/* Account portale (solo edit) */}
            {editingId && effectiveRole !== "employee" && !isExternalConsultant && (
              <TabsContent value="account" className="space-y-3 mt-0">
                <div className="flex items-center gap-2 mb-1">
                  <KeyRound size={16} className="text-suite-primary" />
                  <h3 className="text-sm font-semibold text-suite-neutral-800">Account di accesso al portale</h3>
                  {account.has_account ? (
                    <div className="ml-auto flex items-center gap-2">
                      {account.role && account.role !== "employee" && (
                        <SuiteBadge tone="info" size="sm" testid="acc-role-management">
                          Account aziendale
                        </SuiteBadge>
                      )}
                      <SuiteBadge tone="ok" size="sm" testid="acc-status-active">Attivo</SuiteBadge>
                    </div>
                  ) : (
                    <SuiteBadge tone="neutral" size="sm" className="ml-auto" testid="acc-status-none">Nessun account</SuiteBadge>
                  )}
                </div>
                <p className="text-[11px] text-suite-neutral-500">
                  {account.has_account
                    ? "Il dipendente ha un accesso al portale. Puoi cambiare nome utente, email, generare una nuova password oppure disattivare l'accesso."
                    : "Il dipendente non ha ancora un collegamento. Se nome utente ed email corrispondono a un account aziendale già esistente, verrà collegato senza duplicazioni e senza cambiarne il ruolo."}
                </p>
                <div className="mb-2 px-3 py-2 rounded-md bg-suite-warning-light/60 border border-suite-warning-border text-[11px] text-suite-warning">
                  <strong>Attenzione:</strong> l&apos;account viene salvato con i pulsanti in questa scheda, indipendentemente da &quot;Salva&quot; del dipendente.
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Nome utente</Label>
                    <Input value={accUsername} onChange={(e) => setAccUsername(e.target.value)}
                           placeholder="Generato come nome.cognome" data-testid="emp-acc-username" />
                  </div>
                  <div>
                    <Label className="text-xs">
                      {account.has_account ? "Nuova password (lascia vuoto per non cambiarla)" : "Password iniziale (solo per un nuovo account)"}
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input type="text" value={accPassword} onChange={(e) => setAccPassword(e.target.value)}
                             placeholder={account.has_account ? "•••••••• (invariata)" : "min. 6 caratteri"}
                             data-testid="emp-acc-password" />
                      <SuiteButton type="button" variant="secondary" size="sm" icon={Copy}
                                   onClick={generateRandomPassword} data-testid="emp-acc-generate-pw">
                        Genera
                      </SuiteButton>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-suite-neutral-600">
                  Email per reset e notifiche: <strong>{form.email || "da inserire in Anagrafica"}</strong>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <SuiteButton type="button" size="sm" icon={Save}
                               onClick={saveAccount} loading={accBusy} data-testid="emp-acc-save-btn">
                    {account.has_account ? "Aggiorna account" : "Crea o collega account"}
                  </SuiteButton>
                  {account.has_account && (
                    <SuiteButton type="button" size="sm" variant="secondary" icon={ShieldOff}
                                 onClick={deleteAccount} loading={accBusy}
                                 className="!text-suite-error !border-suite-error/40 hover:!bg-suite-error-light"
                                 data-testid="emp-acc-delete-btn">
                      Rimuovi accesso
                    </SuiteButton>
                  )}
                </div>
              </TabsContent>
            )}
          </Tabs>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 mt-4 border-t border-suite-neutral-100">
            {effectiveRole !== "employee" && (
              <SuiteButton type="button" variant="secondary" size="sm" onClick={() => setOpen(false)} data-testid="cancel-employee-btn">
                Annulla
              </SuiteButton>
            )}
            {!isExternalConsultant && (
              <SuiteButton type="submit" size="sm" icon={Save} data-testid="save-employee-btn">Salva</SuiteButton>
            )}
          </div>
        </form>
      </SafeFormDialog>

      <EmployeeImportDialog
        open={importOpen} onOpenChange={setImportOpen}
        companyId={effectiveRole === "client" ? user.company_id : companyId}
        onImported={load}
      />
    </div>
  );
}
